import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { EmailSchema } from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

const zodAddInvoiceReceiverPayloadSchema = z.object({
  email: EmailSchema,
});

type AddInvoiceReceiverVariables = {
  companyId: UUID;
  email: string;
};

/**
 * Creates a new invoice receiver for a company.
 *
 * @param variables - The variables needed for the mutation.
 * @param variables.companyId - The ID of the company.
 * @param variables.email - The email address to add.
 * @returns A promise that resolves when the invoice receiver is created.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/add_company_invoice_receivers_company__id__invoice_receivers_post
 */
// fallow-ignore-next-line unused-export
export const addCompanyInvoiceReceiver = async ({
  companyId,
  email,
}: AddInvoiceReceiverVariables) => {
  try {
    return await vestigasApi
      .post(ENDPOINT.COMPANY.INVOICE_RECEIVERS.CREATE(companyId), {
        json: { email },
        validate: {
          body: zodAddInvoiceReceiverPayloadSchema,
          response: zodSuccessResponseSchema,
        },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error adding invoice receiver',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query mutation hook for adding an invoice receiver to a company.
 *
 * @param options - Additional options for the useMutation hook.
 * @returns The result of the useMutation hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/add_company_invoice_receivers_company__id__invoice_receivers_post
 */
export const useMutationCompanyAddInvoiceReceiver = (
  options?: Omit<
    UseMutationOptions<SuccessResponse, Error, AddInvoiceReceiverVariables>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<SuccessResponse, Error, AddInvoiceReceiverVariables>({
    mutationFn: addCompanyInvoiceReceiver,
    ...options,
    onSettled(data, error, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getInvoiceReceivers(variables.companyId),
      });

      return options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
  });
};
